import { IconInfoCircle } from '@tabler/icons-react';
import { EmployeePaymentMethodStatus } from '@zorro/clients';
import { Select, type SelectProps } from '@zorro/design-system/v2/Select';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Box, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import { useMonolithMutation, useMonolithQuery } from '@zorro/web/ui-utils';
import { $enum, mapEnumValue } from 'ts-enum-util';

function getPaymentMethodStatusLabel(
  status: EmployeePaymentMethodStatus
): string {
  return mapEnumValue(status).with({
    [EmployeePaymentMethodStatus.EMPTY]: 'No payment method',
    [EmployeePaymentMethodStatus.VALID]: 'Valid',
    [EmployeePaymentMethodStatus.INVALID]: 'Invalid (in-handling)',
    [EmployeePaymentMethodStatus.NEEDS_HANDLING]: 'Needs handling',
  });
}

const EXCLUDED_STATUSES: EmployeePaymentMethodStatus[] = [
  EmployeePaymentMethodStatus.NEEDS_HANDLING,
];

const PAYMENT_METHOD_STATUS_OPTIONS = $enum(EmployeePaymentMethodStatus)
  .getValues()
  .filter((value) => !EXCLUDED_STATUSES.includes(value))
  .map((value) => ({
    value,
    label: getPaymentMethodStatusLabel(value),
  }));

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
};

export const ChangePaymentMethodStatus = ({
  employeeId,
  onboardingPeriodId,
}: Props) => {
  const { data: paymentMethod, isLoading: isLoadingPaymentMethod } =
    useMonolithQuery({
      method: 'paymentsControllerGetPaymentMethod',
      params: [employeeId, onboardingPeriodId],
    });

  const {
    mutate: updatePaymentMethodStatus,
    isPending: isUpdatingPaymentMethodStatus,
  } = useMonolithMutation({
    method: 'paymentsControllerUpdatePaymentMethod',
    shouldInvalidateQueries: true,
  });

  if (isLoadingPaymentMethod || !paymentMethod) {
    return null;
  }

  const { status } = paymentMethod;

  const handlePaymentMethodStatusChange: SelectProps['onChange'] = async (
    value
  ) => {
    const status = $enum(EmployeePaymentMethodStatus).asValueOrDefault(value);
    if (!status) {
      return;
    }

    await updatePaymentMethodStatus([
      employeeId,
      {
        onboardingPeriodId,
        status,
      },
    ]);
  };

  return (
    <Stack gap="xxs">
      <Box>
        <Tooltip
          inline
          label="Changing the status does not automatically affect the payment card/account in ECHO"
        >
          <Group gap="xxs" display="inline-flex">
            <Text size="sm">Change Status</Text>
            <Icon icon={IconInfoCircle} size="0.75rem" />
          </Group>
        </Tooltip>
      </Box>
      <Group>
        <Select
          disabled={isUpdatingPaymentMethodStatus}
          value={status}
          data={PAYMENT_METHOD_STATUS_OPTIONS}
          onChange={handlePaymentMethodStatusChange}
          maw="15.625rem"
        />
      </Group>
    </Stack>
  );
};
